import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { makeStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'

const useStyles = makeStyles(() => ({
  '@keyframes projectsFilterSlideIn': {
    '0%': { transform: 'translateX(100%)' },
    '100%': { transform: 'translateX(0)' },
  },
  backdrop: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    // Above PagCircularIndeterminate (1e7) inside #root so the drawer is never trapped under “searching”
    zIndex: 10000050,
    pointerEvents: 'auto' as const,
  },
  panel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 400,
    maxWidth: '100vw',
    height: '100vh',
    maxHeight: '100dvh',
    backgroundColor: '#fff',
    zIndex: 10000060,
    boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    transform: 'translateX(0)',
    animation: '$projectsFilterSlideIn 280ms cubic-bezier(0.4, 0, 0.2, 1) both',
    pointerEvents: 'auto' as const,
    overflow: 'hidden',
  },
  header: {
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottom: '1px solid #ececf2',
    flexShrink: 0,
  },
  title: {
    fontWeight: 700,
  },
  footerSlot: {
    flexShrink: 0,
  },
  /** Owns vertical scroll; flex basis 0 avoids “infinite content height” bugs with nested lists */
  body: {
    flex: '1 1 0%',
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    position: 'relative',
  },
}))

type Props = {
  open: boolean
  onClose: () => void
  titleId?: string
  title?: string
  children: React.ReactNode
  footer?: React.ReactNode
}

/**
 * Right-hand filter drawer (portal). Backdrop and visible chrome only while `open`.
 *
 * **Children stay mounted when closed** (panel is `display: none`). If we unmounted them,
 * `react-instantsearch` RefinementList connectors would run `cleanUp` and strip those facets
 * from controlled `searchState` right after “Show” applies them.
 */
export function ProjectsFilterSlidePanel({
  open,
  onClose,
  titleId = 'projects-filter-panel-title',
  title = 'Filters',
  children,
  footer,
}: Props) {
  const classes = useStyles()

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  return createPortal(
    <>
      {open ? (
        <div
          className={classes.backdrop}
          role="presentation"
          aria-hidden={false}
          onClick={onClose}
        />
      ) : null}
      <div
        className={classes.panel}
        style={open ? undefined : { display: 'none' }}
        hidden={!open}
        aria-hidden={!open}
        role={open ? 'dialog' : undefined}
        aria-modal={open ? true : undefined}
        aria-labelledby={open ? titleId : undefined}
        onClick={open ? (e) => e.stopPropagation() : undefined}
      >
        {open ? (
          <div className={classes.header}>
            <Typography id={titleId} className={classes.title} component="h2" variant="h6">
              {title}
            </Typography>
            <IconButton size="small" onClick={onClose} aria-label="Close filters">
              <CloseIcon />
            </IconButton>
          </div>
        ) : null}
        <div className={classes.body}>{children}</div>
        {footer != null ? <div className={classes.footerSlot}>{footer}</div> : null}
      </div>
    </>,
    document.body
  )
}
